/**
 * The root route: the one screen nobody chooses to open.
 *
 * Everything else in this app is reached by tapping something. `/` is reached
 * by typing the domain, by a bookmark, by the installed icon, and by every link
 * that lost its path on the way through a mail client, so it is the first thing
 * a person sees and it has to be about them.
 *
 * It was not. The root rendered `ParentHome`, which is a static design mock with
 * a fictional family in it, so the owner signed in as a head coach, opened the
 * domain, and read a stranger's snack duty presented as his app. Nothing was
 * broken in the code and everything was broken on the screen, which is the worse
 * of the two.
 *
 * So `/` decides rather than renders. Per the owner's decision:
 *
 *   - owner, head coach, assistant coach and team manager get `CoachHome`
 *     (ADR-0094), rendered here so the address stays `/`
 *   - a scorekeeper goes to `/team`: the schedule is not part of that role, so
 *     there is no Home to build from, and the shell hides the Home item for
 *     them rather than offering a link that bounces
 *   - a guardian gets `ParentHome`, which is the screen it was drawn for
 *   - nobody signed in goes to `/signin`
 *
 * The decision needs the server, because a role lives on a membership and a
 * client is not allowed to guess one. That means a fetch, which means the states
 * `TeamScreen` already designs, and this file follows it deliberately: same
 * client, same error mapping, same treatment of a 401, same replacing
 * navigation. A person moving between the two screens should not be able to tell
 * that two different files answered.
 */

import { lazy, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { IMPLEMENTED_ROLES, RoleSchema, type Role } from '@good-season/schema';
import { Button, diagnosticFor, errorCopyFor, supportCodeLine } from '@good-season/ui';
import { ApiError, OfflineError, api } from '../api/client.js';
import { AppShell } from '../components/AppShell.js';
import { useSignOut } from '../api/useSignOut.js';
import {
  SAVED_GAME_WAIT_MS,
  resumeLabel,
  savedOnDevice,
  type SavedBook,
} from '../scorebook/saved-on-device.js';
import { LazyScreen, loadChunk } from './LazyScreen.js';

/** Role homes load when needed (ADR-0096 pattern), so the first paint does not carry them. */
const ParentHome = lazy(() =>
  loadChunk(() => import('./ParentHome.js')).then((m) => ({ default: m.ParentHome })),
);
const PendingInvites = lazy(() =>
  loadChunk(() => import('./PendingInvites.js')).then((m) => ({ default: m.PendingInvites })),
);
const CoachHome = lazy(() =>
  loadChunk(() => import('./home/CoachHome.js')).then((m) => ({ default: m.CoachHome })),
);

/** Two letters naming the surface, per `packages/ui/src/primitives/diagnostic.ts`. */
const SURFACE = 'LN';

type Destination = 'team' | 'coach_home' | 'parent_home' | 'not_in_use';

/**
 * Where each role belongs when they arrive with no path.
 *
 * A total `Record<Role, ...>` rather than a condition, for the reason
 * `packages/schema` gives for `ROSTER_ACCESS` and `MEDICAL_ACCESS`: a lookup a
 * reviewer can check against the decision in one glance, and a role added to the
 * enum breaks the build here rather than falling into whichever branch happens
 * to be last. Landing a coach on a parent's screen is the exact defect this
 * route exists to remove, so it must not be reintroducible by omission.
 *
 * `club_admin` is reserved and not implemented in v1. This entry keeps the
 * record total and is never consulted, because the `IMPLEMENTED_ROLES` check in
 * `destinationFor` answers first.
 */
const DESTINATION: Readonly<Record<Role, Destination>> = {
  owner: 'coach_home',
  head_coach: 'coach_home',
  assistant_coach: 'coach_home',
  team_manager: 'coach_home',
  scorekeeper: 'team',
  guardian: 'parent_home',
  club_admin: 'not_in_use',
};

/**
 * Nothing came back that this page can trust.
 *
 * `OfflineError` is a fetch that never arrived: airplane mode, a killed
 * radio. `BADRSP` is a body that is not the API: the preview proxy's error
 * page when the port is closed, a captive portal's login page. The start
 * page still has no role and no schedule, and a book on the phone is still
 * openable. A real refusal (a status and an error code) is not this.
 */
/** A stalled connection must reach the saved-game screen, not wait on the browser's own limit. */
const START_REQUEST_MS = 8_000;

function noAnswer(error: unknown): error is OfflineError | ApiError {
  return (
    error instanceof OfflineError || (error instanceof ApiError && error.diagnostic === 'BADRSP')
  );
}

/**
 * What the start page says when it cannot reach the server.
 *
 * No saved book: the screen that was already there. One or more books: the
 * way into them. A finished book is offered to look at, not to keep scoring.
 */
function offlineBody(games: readonly SavedBook[]): string {
  const open = games.some((game) => game.status !== 'final');
  if (games.length === 1) {
    return open
      ? 'Your saved game is on this phone. Open it and keep scoring. It will sync when you are back on signal.'
      : 'Your finished game is on this phone. Open it to see the score. It will sync when you are back on signal.';
  }
  return open
    ? 'Your saved games are on this phone. Open one and keep scoring. They will sync when you are back on signal.'
    : 'Your finished games are on this phone. Open one to see the score. They will sync when you are back on signal.';
}

/**
 * Read the device's books and hand them over.
 *
 * The first answer wins the screen, including the timeout's empty list, so a
 * stuck database still shows the screen for a phone with no saved book. A
 * read that finishes after that, and actually has a book, replaces it.
 */
async function deliverSavedGames(onGames: (games: readonly SavedBook[]) => void): Promise<void> {
  const read = savedOnDevice().catch(() => [] as readonly SavedBook[]);
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<'timeout'>((resolve) => {
    timer = setTimeout(() => resolve('timeout'), SAVED_GAME_WAIT_MS);
  });
  const first = await Promise.race([
    read.then((games) => ({ kind: 'games' as const, games })),
    timeout,
  ]);
  if (timer !== undefined) clearTimeout(timer);
  if (first !== 'timeout') {
    onGames(first.games);
    return;
  }
  onGames([]);
  onGames(await read);
}

/**
 * Where the role the server named belongs.
 *
 * Two checks rather than one. `RoleSchema` covers a role this build has never
 * heard of, which is a real state and not a hypothetical: the API and the web
 * bundle deploy separately and a service worker can serve a cached bundle for a
 * long time, so a newer server can name a role written after this file.
 * `IMPLEMENTED_ROLES` covers a role that is in the enum and not in use yet.
 * Neither may fall through to a screen built for somebody else, which is what an
 * `else` on `guardian` would do.
 */
function destinationFor(role: string): Destination {
  const parsed = RoleSchema.safeParse(role);
  if (!parsed.success) return 'not_in_use';
  /*
   * Widened so a `Role` can be tested against the implemented subset. The
   * constant is a literal tuple, so `.includes` would otherwise refuse the one
   * value worth asking it about.
   */
  if (!(IMPLEMENTED_ROLES as readonly Role[]).includes(parsed.data)) return 'not_in_use';
  return DESTINATION[parsed.data];
}

type State =
  | { readonly kind: 'loading' }
  | { readonly kind: 'guardian' }
  /** Signed in with an invitation not yet accepted (ADR-0098). */
  | { readonly kind: 'invite_pending'; readonly diagnostic: string }
  | { readonly kind: 'coach' }
  /**
   * A NOTICE and an ERROR, kept apart, the way `TeamScreen` keeps a notice apart
   * from an empty state. They share a frame and differ in the one way that
   * matters to the person reading: an error is worth another go, and a role that
   * is not in use will answer identically on every attempt, so only one of them
   * offers a button. An action that cannot change the outcome is worse than
   * none, because somebody presses it repeatedly before believing it.
   */
  | {
      readonly kind: 'notice';
      readonly title: string;
      readonly body: string;
      readonly diagnostic: string;
    }
  | {
      readonly kind: 'error';
      readonly title: string;
      readonly body: string;
      readonly diagnostic: string;
    }
  /**
   * No signal, and this phone holds a book. The error state above is the
   * dead end for a phone with nothing saved. This one is the way back into
   * the book (issue #208).
   */
  | {
      readonly kind: 'offline';
      readonly diagnostic: string;
      readonly games: readonly SavedBook[];
    };

export function Landing() {
  const navigate = useNavigate();
  const location = useLocation();
  const { search } = location;
  const routeState: unknown = location.state;
  const skipInvitations =
    typeof routeState === 'object' &&
    routeState !== null &&
    (routeState as { skipInvitations?: unknown }).skipInvitations === true;
  const [state, setState] = useState<State>({ kind: 'loading' });
  /** Bumped by Try again, and read by the effect, so a retry is a re-run. */
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        /*
         * A staff invitation waiting on this person comes first (ADR-0097).
         *
         * Somebody who already had an account and was added to a team can only
         * join it by accepting, and before this they had no way to find out it
         * was there: they signed in to their old team, or to a refusal if they
         * had none. Asked before `/api/team` because that refusal is exactly
         * what the second case gets. Best effort for answered failures; a
         * deadline offers Try again rather than silently skipping the check.
         * `skipInvitations` is set by that screen's Not now, so the root does
         * not bounce them straight back.
         */
        if (!skipInvitations) {
          const waiting = await api<{ invitations?: unknown }>(
            '/api/invitations',
            {},
            { timeoutMs: START_REQUEST_MS },
          ).catch((error: unknown) => {
            if (error instanceof OfflineError && error.timedOut) throw error;
            return null;
          });
          if (cancelled) return;
          if (
            waiting !== null &&
            Array.isArray(waiting.invitations) &&
            waiting.invitations.length > 0
          ) {
            // With the query, which holds the selected team: see the /team forward below.
            void navigate({ pathname: '/invitations', search }, { replace: true });
            return;
          }
        }

        /*
         * `/api/team` answers `{ team, role }` and only the role is read here.
         * The team is fetched again by whichever screen wins, which is one extra
         * request on a cold open and buys something worth more than it: this
         * route holds no team state, so it cannot show a stale one, and the
         * screen that renders a team is the screen that asked for it.
         */
        const { role } = await api<{ role: string }>(
          '/api/team',
          {},
          { timeoutMs: START_REQUEST_MS },
        );
        if (cancelled) return;

        const destination = destinationFor(role);

        /*
         * BUILD_PLAN 4.7: a guardian who has not finished the wizard is
         * walked through it rather than dropped on a dashboard. One more
         * request, answered by the server's own record of the step, so a
         * parent who closed the app at step three reopens at step four.
         *
         * For the GUARDIAN destination only. A parent-coach (ADR-0020) is a
         * staff role plus a guardianship, and for one round this redirected
         * them too: a scorekeeper arriving before tip-off was sent into a
         * wizard with no exit instead of to the scorebook, which reversed
         * the decision above that staff go to their team. Graded S1 by the
         * delta pass. Staff land on the team screen, which OFFERS the wizard
         * when they hold an unfinished guardianship.
         *
         * An answered failure still lands on Home. A deadline offers Try again
         * here, since the routing check has not finished. Only a clear
         * "not complete" sends anybody onward.
         */
        if (destination === 'parent_home') {
          const onboarding = await api<{ complete?: unknown }>(
            '/api/guardian/onboarding/complete',
            {},
            { timeoutMs: START_REQUEST_MS },
          ).catch((error: unknown) => {
            if (error instanceof OfflineError && error.timedOut) throw error;
            return null;
          });
          if (cancelled) return;
          if (onboarding !== null && onboarding.complete === false) {
            void navigate({ pathname: '/welcome', search }, { replace: true });
            return;
          }
        }

        if (destination === 'team') {
          /*
           * `replace`, so Back leaves the app rather than returning to a screen
           * whose only behaviour is to forward. Without it the root is a trap:
           * back lands here, here decides again, and here forwards again.
           */
          /*
           * Carrying the query string, because it holds the selected team.
           *
           * A bare `navigate('/team')` dropped `?team=`, so switching teams
           * from the root sent you to `/team` unscoped and the server's own
           * default answered: the switch appeared to do nothing, or worse,
           * silently showed the team you had just switched away from. Graded
           * P1 by the cross-model pass.
           *
           * From `useLocation()`, not `window.location`. My first fix read the
           * window, which is not the router's location under a MemoryRouter, so
           * it was both untestable and wrong in principle inside a component.
           */
          void navigate({ pathname: '/team', search }, { replace: true });
          return;
        }

        if (destination === 'parent_home') {
          setState({ kind: 'guardian' });
          return;
        }

        if (destination === 'coach_home') {
          setState({ kind: 'coach' });
          return;
        }

        const copy = errorCopyFor('ROLE_NOT_IMPLEMENTED');
        setState({
          kind: 'notice',
          title: copy.title,
          body: copy.body,
          // Derived from the role rather than generated, so two people reporting
          // the same code are reporting the same thing.
          diagnostic: diagnosticFor(`role:${role}`, SURFACE),
        });
      } catch (error) {
        if (cancelled) return;

        // Not signed in is not an error state, it is a different screen.
        if (
          error instanceof ApiError &&
          (error.code === 'AUTH_REQUIRED' || error.code === 'AUTH_SESSION_EXPIRED')
        ) {
          void navigate('/signin', { replace: true });
          return;
        }

        if (error instanceof ApiError && error.code === 'INVITE_PENDING') {
          setState({ kind: 'invite_pending', diagnostic: diagnosticFor(error, SURFACE) });
          return;
        }

        if (noAnswer(error)) {
          const diagnostic = diagnosticFor(error, SURFACE);
          const offline = error instanceof OfflineError;
          /*
           * The book is already on this phone. Ask the device before painting
           * the dead end, and do not wait forever if the database will not
           * answer: the existing screen is still the right one when nothing
           * is saved.
           */
          await deliverSavedGames((games) => {
            if (cancelled) return;
            if (games.length > 0) {
              setState({ kind: 'offline', diagnostic, games });
              return;
            }
            if (offline) {
              setState({
                kind: 'error',
                title: 'No connection right now.',
                body: 'Good Season opens as soon as you are back on signal.',
                diagnostic,
              });
              return;
            }
            const copy = errorCopyFor(error instanceof ApiError ? error.code : undefined);
            setState({
              kind: 'error',
              title: copy.title,
              body: copy.body,
              diagnostic,
            });
          });
          return;
        }

        const copy = error instanceof ApiError ? errorCopyFor(error.code) : errorCopyFor();
        setState({
          kind: 'error',
          title: copy.title,
          body: copy.body,
          /*
           * `diagnosticFor` on every branch, rather than `error.diagnostic` on
           * the ones that carry one. `CLAUDE.md` wants a short code a coach can
           * send to support on error copy, and the failures most likely to be
           * reported (a dead network, an exception with no envelope) are exactly
           * the ones with nothing from the server to print. This one synthesises
           * a deterministic code from the error itself instead of printing
           * nothing. Same treatment as `PlayerSheet` and `AddPersonSheet`.
           */
          diagnostic: diagnosticFor(error, SURFACE),
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [navigate, attempt, search, skipInvitations]);

  /*
   * The root needs its own sign out.
   *
   * `ParentHome` links to the schedule now and nowhere else, and contains no
   * sign-out of its own, so before the shell reached this route a guardian who
   * opened the bare domain had no way to leave. The shell offers the control;
   * this supplies the action behind it.
   */
  const signOut = useSignOut();

  function retry() {
    /*
     * Back to the designed loading screen first. Leaving the failure on screen
     * while the second attempt runs makes the button look dead and invites a
     * second press, which is how one failed open becomes three requests.
     */
    setState({ kind: 'loading' });
    setAttempt((previous) => previous + 1);
  }

  if (state.kind === 'loading') {
    /*
     * A screen, not a spinner and above all not `null`.
     *
     * A blank root while the answer is in flight is the same bug as the wrong
     * root: the person cannot tell a working app from a broken one. It also has
     * to say Good Season, because on a cold open this is the first paint and
     * "which app is this" is a real question when the icon was tapped by
     * accident.
     */
    return (
      <AppShell onSignOut={() => void signOut()}>
        <div className="team">
          <p className="team__eyebrow">Good Season</p>
          <h2 className="team__name">One moment.</h2>
          <p className="team__body">Opening your season.</p>
        </div>
      </AppShell>
    );
  }

  /*
   * The guardian screen gets the frame too, and this is the half of the shell
   * that did not land the first time. The complaint that started this work was
   * about the bare domain, and wrapping only `/team` left the screen actually
   * being complained about exactly as it was.
   */
  if (state.kind === 'guardian') {
    return (
      <LazyScreen chrome="shell">
        <AppShell onSignOut={() => void signOut()}>
          <ParentHome />
        </AppShell>
      </LazyScreen>
    );
  }

  if (state.kind === 'invite_pending') {
    return (
      <LazyScreen chrome="shell">
        <AppShell onSignOut={() => void signOut()}>
          <PendingInvites diagnostic={state.diagnostic} />
        </AppShell>
      </LazyScreen>
    );
  }

  /*
   * A book on this phone, and no way to ask the server for Home. The link
   * opens the local scorebook. Try again is how Home loads once signal
   * returns, and it is not the primary action: the game is the thing they
   * opened the app to reach.
   */
  if (state.kind === 'offline') {
    const alone = state.games.length === 1;
    return (
      <AppShell onSignOut={() => void signOut()}>
        <div className="team">
          <p className="team__eyebrow">Good Season</p>
          <h2 className="team__name">No connection right now.</h2>
          <p className="team__body">{offlineBody(state.games)}</p>
          <ul className="team__saved" aria-label="Saved games on this phone">
            {state.games.map((game) => (
              <li key={game.id}>
                <Link className="home__button" to={{ pathname: `/scorebook/${game.id}`, search }}>
                  {resumeLabel(game, alone)}
                </Link>
              </li>
            ))}
          </ul>
          <p className="team__code">{supportCodeLine(state.diagnostic)}</p>
          <footer className="team__footer">
            <Button tone="ghost" onClick={retry}>
              Try again
            </Button>
          </footer>
        </div>
      </AppShell>
    );
  }

  /* The coach screen draws its own shell: it knows the team, this route does not. */
  if (state.kind === 'coach') {
    /*
     * Keyed by the selected team, as a second guard. In the app, `App.tsx`
     * renders `<Routes key={team}>`, so a switch from the shell's switcher
     * already remounts this whole route, Landing included. This key keeps the
     * Home correct if `Landing` is ever mounted without that outer key: the
     * switcher changes `?team=` on the same route, and without a remount every
     * card would keep the previous team's data beside links for the new one.
     */
    return (
      <LazyScreen chrome="shell">
        <CoachHome
          key={new URLSearchParams(search).get('team') ?? ''}
          onSignOut={() => void signOut()}
        />
      </LazyScreen>
    );
  }

  return (
    <AppShell onSignOut={() => void signOut()}>
      <div className="team">
        <p className="team__eyebrow">Good Season</p>
        <h2 className="team__name">{state.title}</h2>
        <p className="team__body">{state.body}</p>
        <p className="team__code">{supportCodeLine(state.diagnostic)}</p>
        {state.kind === 'error' ? (
          <footer className="team__footer">
            <Button onClick={retry}>Try again</Button>
          </footer>
        ) : null}
      </div>
    </AppShell>
  );
}
